import { lookOf, type SettingLookCode } from '@gk/contracts'
import type { GeoJsonGeometry, Pos } from '../lib/geo.ts'
import type { FeatureCollectionOf } from './geo.ts'
import { EARTH_RADIUS_M, targetPoint } from './guide.ts'
import { lookForChoice, type JobState, type TaskTypeRow } from './plan.ts'
import type { PlayLine } from './playLine.ts'

/** M25-L (docs/M25.md C, rule 5; ADR-132): « SENS DU JOUR » ON THE MAP — pure, tested in lookMap.test.ts. For each area of the day's jobs whose settings
 *  hold a direction or pattern PICTURE (read through `lookForChoice`: the club's word means nothing, the picture's declared meaning is read) and whose line
 *  of play is known, the axis is drawn across its real outline: the line through the centroid at the area's line of play + the picture's angle in the
 *  area's own clock (12 = the way the play travels, clockwise; so 3 o'clock is to the right of the play seen from 6), cut where it leaves the outline
 *  (the chord around the centroid; the whole span when the centroid lies outside the shape, a concave one or a hole); one arrowhead for a first pass toward
 *  a heading, two for an axis. A checkerboard or a diamond draws its two axes; 50/50 and up-and-back the line of play; around the outline and the laps and
 *  groomers draw nothing (no axis). An area whose line of play is unknown is LEFT OUT and counted (the page says « n zones sans sens de jeu »). Lines
 *  only: no text, no symbol (the style has no glyphs). The line of play turns this drawing and decides nothing. */

export interface AxisOfLook { angle: number; heads: 1 | 2 }
/** The axes a picture lays on the map, in the area's clock (degrees clockwise from 12); none for a picture with no axis. */
export function axesOf(code: string): AxisOfLook[] {
  const l = lookOf(code)
  if (!l) return []
  if (l.family === 'dir') return l.heading === null ? [{ angle: l.axis, heads: 2 }] : [{ angle: l.heading, heads: 1 }]
  if (l.family !== 'pat') return []
  switch (l.pattern) {
    case 'stripes': return [{ angle: l.angle ?? 0, heads: 2 }]
    case 'checker': return [{ angle: 0, heads: 2 }, { angle: 90, heads: 2 }]
    case 'diamond': return [{ angle: 45, heads: 2 }, { angle: 135, heads: 2 }]
    case 'half': case 'box': return [{ angle: 0, heads: 2 }]
    default: return []
  }
}

const M_PER_DEG = (EARTH_RADIUS_M * Math.PI) / 180
const rad = (d: number) => (d * Math.PI) / 180
type V = { x: number; y: number }

function rings(geom: GeoJsonGeometry): Pos[][] | null {
  if (geom.type === 'Polygon') return geom.coordinates as Pos[][]
  if (geom.type === 'MultiPolygon') return (geom.coordinates as Pos[][][]).flat()
  return null
}

/** The chord of a shape through its centroid along an azimuth (degrees clockwise from north): its two ends, the first point going backward and the first
 *  going forward where it leaves the outline; null for a shape that is not a polygon or a line that never crosses it. The first end is behind, the second
 *  ahead (in the azimuth's sense). */
export function chordThrough(geom: GeoJsonGeometry | null | undefined, azimuth: number): [Pos, Pos] | null {
  if (!geom) return null
  const rs = rings(geom)
  const c = rs ? targetPoint(geom) : null
  if (!rs || !c) return null
  const kx = M_PER_DEG * Math.cos(rad(c[1]))
  const toV = (p: Pos): V => ({ x: (p[0] - c[0]) * kx, y: (p[1] - c[1]) * M_PER_DEG })
  const toP = (v: V): Pos => [c[0] + v.x / kx, c[1] + v.y / M_PER_DEG]
  const d: V = { x: Math.sin(rad(azimuth)), y: Math.cos(rad(azimuth)) }
  const ts: number[] = []
  let crossings = 0                                                     // a ray to +∞ along the perpendicular: even-odd, is the centroid inside?
  for (const ring of rs) {
    const pts = ring.map(toV)
    for (let i = 0; i + 1 < pts.length; i++) {
      const a = pts[i]!, b = pts[i + 1]!, e = { x: b.x - a.x, y: b.y - a.y }
      const den = d.x * e.y - d.y * e.x
      if (Math.abs(den) > 1e-12) {
        const t = (a.x * e.y - a.y * e.x) / den, s = (a.x * d.y - a.y * d.x) / den
        if (s >= 0 && s < 1) ts.push(t)
      }
      if ((a.y > 0) !== (b.y > 0) && a.x + ((0 - a.y) * (b.x - a.x)) / (b.y - a.y) > 0) crossings++
    }
  }
  if (ts.length < 2) return null
  const inside = crossings % 2 === 1
  const back = inside ? Math.max(...ts.filter((t) => t <= 0)) : Math.min(...ts)
  const ahead = inside ? Math.min(...ts.filter((t) => t > 0)) : Math.max(...ts)
  if (!Number.isFinite(back) || !Number.isFinite(ahead) || ahead - back < 1e-6) return null
  return [toP({ x: d.x * back, y: d.y * back }), toP({ x: d.x * ahead, y: d.y * ahead })]
}

export type LookFeature = FeatureCollectionOf['features'][number] & { properties: { area_id: string; job_id: string; code: string; part: 'shaft' | 'head' } }

/** An arrowhead at `tip` pointing along the azimuth `az`: two short arms, as one MultiLineString. */
function head(tip: Pos, az: number, lengthM: number): GeoJsonGeometry {
  const kx = M_PER_DEG * Math.cos(rad(tip[1]))
  const arm = (side: number): Pos => { const b = rad(az + 180 + side * 28); return [tip[0] + (lengthM * Math.sin(b)) / kx, tip[1] + (lengthM * Math.cos(b)) / M_PER_DEG] }
  return { type: 'MultiLineString', coordinates: [[tip, arm(1)], [tip, arm(-1)]] }
}

/** The features of one picture on one area: per axis a shaft (the chord) and its arrowheads. */
export function lookAxisFeatures(areaId: string, jobId: string, code: SettingLookCode | string, geom: GeoJsonGeometry, bearing: number): LookFeature[] {
  const out: LookFeature[] = []
  for (const { angle, heads } of axesOf(code)) {
    const az = (((bearing + angle) % 360) + 360) % 360
    const ch = chordThrough(geom, az)
    if (!ch) continue
    const props = { area_id: areaId, job_id: jobId, code }
    const id = `${areaId}:${jobId}:${code}:${angle}`
    out.push({ type: 'Feature', id: `${id}:shaft`, geometry: { type: 'LineString', coordinates: ch }, properties: { ...props, part: 'shaft' } })
    const kx = M_PER_DEG * Math.cos(rad(ch[0][1]))
    const len = Math.hypot((ch[1][0] - ch[0][0]) * kx, (ch[1][1] - ch[0][1]) * M_PER_DEG)
    const h = Math.min(8, Math.max(1.5, 0.18 * len))
    out.push({ type: 'Feature', id: `${id}:head1`, geometry: head(ch[1], az, h), properties: { ...props, part: 'head' } })
    if (heads === 2) out.push({ type: 'Feature', id: `${id}:head2`, geometry: head(ch[0], (az + 180) % 360, h), properties: { ...props, part: 'head' } })
  }
  return out
}

export interface DayLookJob { id: string; task_type_code: string; params: Record<string, string | number>; state: JobState }
/** The day's jobs per area (data/mapJobs.ts `jobsByArea`) → the `looks-axis` collection, the areas drawn and the areas left out (a picture, but no line of
 *  play), each list sorted. A cancelled job draws nothing. */
export function dayLookFeatures(byArea: ReadonlyMap<string, readonly DayLookJob[]>, taskTypes: readonly TaskTypeRow[], geoms: ReadonlyMap<string, GeoJsonGeometry>,
  lines: ReadonlyMap<string, Pick<PlayLine, 'bearing'>>): { collection: { type: 'FeatureCollection'; features: LookFeature[] }; drawn: string[]; leftOut: string[] } {
  const tt = new Map(taskTypes.map((t) => [t.code, t]))
  const features: LookFeature[] = []; const drawn = new Set<string>(); const leftOut = new Set<string>()
  for (const [areaId, jobs] of byArea) {
    for (const j of jobs) {
      if (j.state === 'cancelled') continue
      const type = tt.get(j.task_type_code)
      // two settings of one job may carry the same picture: it is drawn once (a feature id is area:job:code:angle, never twice)
      const codes = [...new Set(Object.entries(j.params ?? {}).map(([k, v]) => lookForChoice(type, k, v)).filter((c): c is SettingLookCode => !!c && axesOf(c).length > 0))]
      if (!codes.length) continue
      const bearing = lines.get(areaId)?.bearing ?? null
      const geom = geoms.get(areaId)
      if (bearing === null || !geom) { leftOut.add(areaId); continue }
      for (const code of codes) {
        const f = lookAxisFeatures(areaId, j.id, code, geom, bearing)
        if (f.length) { features.push(...f); drawn.add(areaId) }
      }
    }
  }
  for (const a of drawn) leftOut.delete(a)
  return { collection: { type: 'FeatureCollection', features }, drawn: [...drawn].sort(), leftOut: [...leftOut].sort() }
}
